import { useCallback, useEffect, useRef, useState } from 'react';
import { fetcher } from 'services';
import useMyInfo from './useMyInfo';
import { safeJsonParse } from '../utils/LGLifeUtils';

/**
 * 이어보기 조회/기록 훅.
 * mediaIds로 넘어온 항목 중 아직 조회하지 않은 것만 배치로 조회한다.
 * (스크롤로 목록이 늘어나면 새로 늘어난 mediaId만 추가 조회됨)
 */
const useContinue = (mediaIds: string[] = []) => {
  const { info } = useMyInfo();

  const [serverMap, setServerMap] = useState<Record<string, number>>({});
  const [localMap, setLocalMap] = useState<Record<string, number>>({});
  const fetchedIds = useRef<Set<string>>(new Set());

  const getlocalContinue = useCallback((): MediaContinueReqDto[] => {
    return safeJsonParse(localStorage.getItem('continue') || JSON.stringify([]));
  }, []);

  const setLocalContinue = useCallback((data: MediaContinueReqDto[]) => {
    localStorage.setItem('continue', JSON.stringify(data));
  }, []);

  useEffect(() => {
    const local = getlocalContinue();
    const map: Record<string, number> = {};
    local?.forEach(item => {
      map[item.mediaId] = item.timeLine;
    });
    setLocalMap(map);
  }, [getlocalContinue]);

  useEffect(() => {
    if (!info) return;

    const newIds = Array.from(new Set(mediaIds.filter(id => id && !fetchedIds.current.has(id))));
    if (newIds.length === 0) return;

    fetcher.media
      .getMediaContinueByIds(newIds)
      .then((res: any) => {
        newIds.forEach(id => fetchedIds.current.add(id));

        const updates: Record<string, number> = {};
        res?.mediaContinueList?.forEach((item: MediaContinueResDto) => {
          if (item.mediaId) {
            updates[item.mediaId] = item.timeLine || 0;
          }
        });
        if (Object.keys(updates).length) {
          setServerMap(prev => ({ ...prev, ...updates }));
        }
      })
      .catch(err => {
        console.error('미디어 이어보기 배치 조회 실패', err);
      });
  }, [info, mediaIds]);

  const postMediaContinue = useCallback(
    (params: MediaContinueReqDto[]) =>
      fetcher.media
        .postMediaContinue({
          mediaContinueReqList: params,
        })
        .then(res => {
          if (res.resultCode === 200) {
            setServerMap(prev => {
              const next = { ...prev };
              params.forEach(item => {
                next[item.mediaId] = item.timeLine;
              });
              return next;
            });
            setLocalContinue([]);
            setLocalMap({});
          }
        }),
    [setLocalContinue]
  );

  const getTimeLine = useCallback(
    (mediaId?: string): number => {
      if (!mediaId) return 0;
      return localMap[mediaId] ?? serverMap[mediaId] ?? 0;
    },
    [localMap, serverMap]
  );

  const updateTime = useCallback(
    (mediaId: string, time: number) => {
      const localContinue = getlocalContinue();
      let targetIndex = localContinue?.length;

      localContinue?.some((local, index) => {
        if (local.mediaId === mediaId) {
          targetIndex = index;
          return true;
        }
        return false;
      });

      localContinue?.splice(targetIndex, 1, { mediaId: mediaId, timeLine: time } as MediaContinueReqDto);
      setLocalContinue(localContinue);
    },
    [getlocalContinue, setLocalContinue]
  );

  const updateMediaContinue = useCallback(() => {
    const localContinue = getlocalContinue();
    if (localContinue?.length > 0) {
      postMediaContinue(localContinue);
    }
  }, [getlocalContinue, postMediaContinue]);

  return { getTimeLine, updateTime, updateMediaContinue };
};

export default useContinue;